import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileJson, Users, FileText, FileCheck, Database, UserCog, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExportPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState({});
  const [reparti, setReparti] = useState([]);
  const [selectedReparti, setSelectedReparti] = useState([]); // Array di ID selezionati (vuoto = tutti)
  
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Fetch reparti per Super Admin (tutti, inclusa Sede Centrale)
  useEffect(() => {
    const fetchReparti = async () => {
      if (!isSuperAdmin) return;
      try {
        const response = await axios.get(`${API}/reparti/all-for-filters`, { headers: getAuthHeader() });
        setReparti(response.data);
      } catch (error) {
        console.error('Error fetching reparti:', error);
      }
    };
    fetchReparti();
  }, [isSuperAdmin, getAuthHeader]);

  // Toggle selezione reparto
  const toggleReparto = (repartoId) => {
    setSelectedReparti(prev => 
      prev.includes(repartoId) 
        ? prev.filter(id => id !== repartoId)
        : [...prev, repartoId]
    );
  };

  // Seleziona/Deseleziona tutti
  const toggleAll = () => {
    if (selectedReparti.length === reparti.length) {
      setSelectedReparti([]);
    } else {
      setSelectedReparti(reparti.map(r => r.id));
    }
  };

  // Nuovo export Excel con filtro reparto multiplo
  const handleExportExcel = async (type) => {
    setLoading(prev => ({ ...prev, [`${type}_xlsx`]: true }));
    
    try {
      let url = `${API}/export/${type}`;
      // Se ci sono reparti selezionati, passali come query param
      if (isSuperAdmin && selectedReparti.length > 0 && selectedReparti.length < reparti.length) {
        url += `?reparto_ids=${selectedReparti.join(',')}`;
      }
      
      const response = await axios.get(url, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlObj;
      const filename = `export_${type}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${type} Excel completato`);
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 404) {
        toast.error('Nessun dato trovato per l\'export');
      } else {
        toast.error('Errore durante l\'export');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`${type}_xlsx`]: false }));
    }
  };

  const handleExport = async (type, format) => {
    setLoading(prev => ({ ...prev, [`${type}_${format}`]: true }));
    
    try {
      const response = await axios.get(`${API}/export/${type}?format=${format}`, {
        headers: getAuthHeader(),
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Export ${type} CSV completato`);
      } else {
        // JSON - download as file
        const jsonStr = JSON.stringify(response.data.data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filename = `${type}_${new Date().toISOString().split('T')[0]}.json`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Export ${type} JSON completato`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export');
    } finally {
      setLoading(prev => ({ ...prev, [`${type}_${format}`]: false }));
    }
  };

  if (!['admin', 'super_admin', 'top_agent'].includes(user?.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Accesso non autorizzato</p>
      </div>
    );
  }

  const exportCards = [
    {
      type: 'users',
      title: 'Utenti Sistema',
      description: 'Esporta tutti gli utenti (Admin, Agenti, Clienti)',
      icon: UserCog,
      color: 'bg-violet-500'
    },
    {
      type: 'contacts',
      title: 'Rubrica Clienti',
      description: 'Esporta tutti i contatti (Smart e Prospect)',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      type: 'offers',
      title: 'Elenco Offerte',
      description: 'Esporta tutte le offerte in stato "offerta"',
      icon: FileText,
      color: 'bg-amber-500'
    },
    {
      type: 'orders',
      title: 'Elenco Ordini',
      description: 'Esporta tutti i contratti validati',
      icon: FileCheck,
      color: 'bg-emerald-500'
    }
  ];

  // Export Excel con filtro reparto
  const excelExportCards = [
    {
      type: 'contacts',
      title: 'Rubrica Clienti (Excel)',
      description: 'Esporta contatti in formato Excel',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      type: 'contracts',
      title: 'Portafoglio Contratti (Excel)',
      description: 'Esporta contratti in formato Excel',
      icon: FileCheck,
      color: 'bg-emerald-500'
    },
    {
      type: 'users',
      title: 'Utenti (Excel)',
      description: 'Esporta utenti in formato Excel',
      icon: UserCog,
      color: 'bg-violet-500'
    }
  ];

  return (
    <div className="space-y-6" data-testid="export-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Backup & Export
        </h1>
        <p className="text-slate-500 mt-1">Esporta i dati del sistema in formato Excel, CSV o JSON</p>
      </div>

      {/* Filtro Reparto per Super Admin - Selezione Multipla */}
      {isSuperAdmin && reparti.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Building2 className="w-6 h-6 text-amber-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Filtro Reparti (Super Admin)</h3>
                <p className="text-sm text-amber-700 mb-3">Seleziona i reparti da includere negli export Excel</p>
                
                {/* Checkbox Grid */}
                <div className="bg-white rounded-lg border border-amber-200 p-3">
                  {/* Seleziona Tutti */}
                  <label className="flex items-center gap-2 p-2 hover:bg-amber-50 rounded cursor-pointer border-b border-amber-100 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedReparti.length === 0 || selectedReparti.length === reparti.length}
                      onChange={toggleAll}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <span className="font-medium text-slate-700">
                      {selectedReparti.length === 0 ? '✓ Tutti i reparti (default)' : 'Seleziona tutti'}
                    </span>
                  </label>
                  
                  {/* Lista Reparti */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {reparti.map(reparto => (
                      <label 
                        key={reparto.id} 
                        className="flex items-center gap-2 p-2 hover:bg-amber-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedReparti.includes(reparto.id)}
                          onChange={() => toggleReparto(reparto.id)}
                          className="w-4 h-4 text-amber-600 rounded"
                        />
                        <span className={`text-sm ${reparto.is_sede_centrale ? 'font-semibold text-amber-800' : 'text-slate-700'}`}>
                          {reparto.nome}
                          {reparto.is_sede_centrale && <span className="text-xs text-amber-600 ml-1">(Sede)</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Info selezione */}
                <p className="text-xs text-amber-600 mt-2">
                  {selectedReparti.length === 0 
                    ? 'Export includerà tutti i reparti'
                    : `Export includerà ${selectedReparti.length} reparto/i selezionato/i`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Excel Section */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5" />
          Export Excel (Multitenancy)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {excelExportCards.map(({ type, title, description, icon: Icon, color }) => (
            <Card key={`xlsx-${type}`} className="relative overflow-hidden">
              <div className={`absolute top-0 left-0 w-1 h-full ${color}`} />
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
                    <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full gap-2"
                  onClick={() => handleExportExcel(type)}
                  disabled={loading[`${type}_xlsx`]}
                  data-testid={`export-${type}-xlsx`}
                >
                  <Download className="w-4 h-4" />
                  {loading[`${type}_xlsx`] ? 'Esportando...' : 'Scarica Excel'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Database className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Area Backup Legacy</h3>
              <p className="text-sm text-slate-600 mt-1">
                Export in formato CSV/JSON per backup o integrazioni.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {exportCards.map(({ type, title, description, icon: Icon, color }) => (
          <Card key={type} className="relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-1 h-full ${color}`} />
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
                  <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
                </div>
                <div>
                  <CardTitle className="text-lg">{title}</CardTitle>
                  <CardDescription>{description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => handleExport(type, 'csv')}
                  disabled={loading[`${type}_csv`]}
                  data-testid={`export-${type}-csv`}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  {loading[`${type}_csv`] ? 'Esportando...' : 'CSV'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => handleExport(type, 'json')}
                  disabled={loading[`${type}_json`]}
                  data-testid={`export-${type}-json`}
                >
                  <FileJson className="w-4 h-4" />
                  {loading[`${type}_json`] ? 'Esportando...' : 'JSON'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Legenda Formati</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-800">CSV (Comma Separated Values)</p>
                <p className="text-sm text-slate-500">
                  Formato tabellare compatibile con Excel, Google Sheets e altri fogli di calcolo.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <FileJson className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-800">JSON (JavaScript Object Notation)</p>
                <p className="text-sm text-slate-500">
                  Formato strutturato ideale per backup completi e integrazioni con altri sistemi.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportPage;
